import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, Spinner } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import {
  addNoticeAttachment,
  createNotice,
  getNoticeAttachmentUrl,
  getNoticeOptions,
  getNoticeProperties,
  listNotices,
  polishNotice,
  previewNoticeAudience,
  removeNoticeAttachment,
  sendNotice,
  sendNoticeTest,
  undoNoticeSend,
  updateNotice,
  uploadNoticeFile,
} from "../noticesApi";
import { canSendCompanyWide } from "../access";
import { AudiencePicker } from "./AudiencePicker";
import { NoticeHistory } from "./NoticeHistory";
import NoticePreview from "./NoticePreview";
import SendConfirm, { UndoBar, needsConfirm } from "./SendConfirm";
import TemplatePicker from "./TemplatePicker";
import { isPhotoFile, isVideoFile, makeVideoPoster, makeWebCopy } from "./mediaDerivatives";
import {
  MAX_NOTICE_FILES,
  MAX_NOTICE_VIDEOS,
  MAX_NOTICE_FILE_BYTES,
  MAX_NOTICE_VIDEO_BYTES,
  VIDEO_EXTENSIONS,
  NOTICE_TYPES,
  TYPE_ICON,
  emptyNotice,
  eventDateEndIso,
  formatDateOnly,
  formatBytes,
  formatWhen,
  isAcceptedFile,
  recurrencePlan,
  toLocalDateValue,
  toLocalInputValue,
} from "./noticeHelpers";
import "./notices.scss";

const AUTOSAVE_MS = 800;

const sameAudience = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/**
 * The web-sized photo copy and the video poster are bookkeeping made by the browser, not files
 * the manager chose. They never show in the list and never count against the file limit — the
 * server counts them the same way.
 */
const isDerivativeAttachment = (a) => a?.kind === "inline" || a?.kind === "poster";

/**
 * Send Notices. One modal, two tabs: Compose (the notice, who gets it, when and how it goes
 * out) and History (everything sent, scheduled or drafted for this building). Drafts save
 * themselves as you type; files go to storage first and are then recorded on the draft.
 */
const SendNoticeModal = ({ isOpen, onClose, filterData: pickedPlace, userData, initialTab = "compose", onSent, allProperties = false }) => {
  const canCompany = canSendCompanyWide(userData);
  // "All Properties" in the directory (company login only): the notice can only be company-wide.
  // Every property-bound call below reads `filterData`, so in that mode it carries no property —
  // the directory's "__ALL__" marker never reaches the server as a property id.
  const allMode = allProperties && canCompany;
  const filterData = useMemo(
    () => (allMode ? { ...pickedPlace, property_id: "", property_name: "", building_name: "" } : pickedPlace),
    [allMode, pickedPlace]
  );
  const [tab, setTab] = useState(initialTab);

  // ---- the draft --------------------------------------------------------
  const [draft, setDraft] = useState(emptyNotice);
  const [draftId, setDraftId] = useState(null);
  const [scope, setScope] = useState("property");
  const [propertyIds, setPropertyIds] = useState([]);
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | error
  const [savedAt, setSavedAt] = useState(null);
  const draftIdRef = useRef(null);
  const dirtyRef = useRef(false);
  const creatingRef = useRef(null);

  // ---- what the picker needs ---------------------------------------------
  const [options, setOptions] = useState(null);
  const [properties, setProperties] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [drafts, setDrafts] = useState([]);

  // ---- delivery ----------------------------------------------------------
  const [when, setWhen] = useState("now"); // now | later
  const [scheduledAt, setScheduledAt] = useState("");
  const [scheduleMode, setScheduleMode] = useState("once"); // once | recurring
  const [intervalDays, setIntervalDays] = useState("4");
  const [eventDate, setEventDate] = useState("");
  const [busy, setBusy] = useState(false); // false | "send" | "test" | "upload" | "polish"
  const [uploadPct, setUploadPct] = useState(0);
  const [historyKey, setHistoryKey] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const sentIdRef = useRef(null);
  const sentDataRef = useRef(null);
  const fileInputRef = useRef(null);
  const polishUndoRef = useRef(null);
  const [canUndoPolish, setCanUndoPolish] = useState(false);

  const buildingLabel = [filterData.property_name, filterData.building_name].filter(Boolean).join(" · ");

  const targetPayload = useCallback(
    () =>
      scope === "company" || allMode
        ? { scope: "company", property_ids: propertyIds }
        : { scope: "property", property_id: filterData.property_id, property_name: filterData.property_name, building_name: filterData.building_name || "" },
    [scope, allMode, propertyIds, filterData.property_id, filterData.property_name, filterData.building_name]
  );

  const resetCompose = useCallback(() => {
    setDraft(emptyNotice());
    setDraftId(null);
    draftIdRef.current = null;
    dirtyRef.current = false;
    setSaveState("idle");
    setSavedAt(null);
    setScope(allMode ? "company" : "property");
    setPropertyIds([]);
    setWhen("now");
    setScheduledAt("");
    setScheduleMode("once");
    setIntervalDays("4");
    setEventDate("");
    setPreview(null);
    setShowPreview(false);
    setConfirming(false);
    polishUndoRef.current = null;
    setCanUndoPolish(false);
  }, [allMode]);

  // Open: fresh compose state, load the picker data and any drafts waiting here.
  useEffect(() => {
    if (!isOpen) return;
    setTab(initialTab);
    resetCompose();
    if (filterData.property_id) {
      getNoticeOptions(filterData.property_id, filterData.building_name)
        .then((r) => setOptions(r?.data || null))
        .catch(() => setOptions(null));
      listNotices({ property_id: filterData.property_id, building_name: filterData.building_name || "", status: "draft" })
        .then((r) => setDrafts(r?.data || []))
        .catch(() => setDrafts([]));
    }
    if (canCompany) {
      getNoticeProperties()
        .then((r) => setProperties(r?.data?.properties || []))
        .catch(() => setProperties([]));
    }
  }, [isOpen, initialTab, filterData.property_id, filterData.building_name, canCompany, resetCompose]);

  // Live audience count, debounced, whenever the audience or scope changes.
  useEffect(() => {
    if (!isOpen || tab !== "compose") return undefined;
    if (scope === "property" && !filterData.property_id) return undefined;
    setPreviewLoading(true);
    const t = setTimeout(() => {
      previewNoticeAudience({ ...targetPayload(), audience: draft.audience })
        .then((r) => setPreview(r?.data || null))
        .catch(() => setPreview(null))
        .finally(() => setPreviewLoading(false));
    }, 500);
    return () => clearTimeout(t);
  }, [isOpen, tab, scope, draft.audience, targetPayload, filterData.property_id]);

  // ---- autosave -----------------------------------------------------------
  const meaningful = draft.title.trim() || draft.message.trim() || draft.attachments.length > 0;

  const persist = useCallback(async () => {
    const body = {
      ...targetPayload(),
      type: draft.type,
      title: draft.title,
      message: draft.message,
      audience: draft.audience,
      options: { sms: !!draft.options.sms, require_acknowledgement: !!draft.options.require_acknowledgement },
    };
    setSaveState("saving");
    try {
      let res;
      if (draftIdRef.current) {
        res = await updateNotice(draftIdRef.current, body);
      } else {
        if (!creatingRef.current) creatingRef.current = createNotice(body);
        res = await creatingRef.current;
        creatingRef.current = null;
        draftIdRef.current = res?.data?._id || null;
        setDraftId(draftIdRef.current);
      }
      setSaveState("saved");
      setSavedAt(new Date());
      return res?.data;
    } catch (e) {
      setSaveState("error");
      creatingRef.current = null;
      throw e;
    }
  }, [draft, targetPayload]);

  useEffect(() => {
    if (!isOpen || !dirtyRef.current || !meaningful) return undefined;
    const t = setTimeout(() => {
      dirtyRef.current = false;
      persist().catch(() => {});
    }, AUTOSAVE_MS);
    return () => clearTimeout(t);
  }, [isOpen, draft.type, draft.title, draft.message, draft.audience, draft.options.sms, draft.options.require_acknowledgement, scope, propertyIds, meaningful, persist]);

  const edit = (patch) => {
    dirtyRef.current = true;
    setDraft((d) => ({ ...d, ...patch }));
  };
  const editAudience = (audience) => {
    if (sameAudience(audience, draft.audience)) return;
    edit({ audience });
  };

  const handlePolish = async () => {
    if (!draft.title.trim() && !draft.message.trim()) return toast.error("Write a title or a message first.");
    setBusy("polish");
    try {
      const res = await polishNotice({ title: draft.title, message: draft.message, type: draft.type });
      const next = res?.data;
      if (!next || (typeof next.title !== "string" && typeof next.message !== "string")) {
        throw new Error("The polish did not return usable text.");
      }
      polishUndoRef.current = { title: draft.title, message: draft.message };
      setCanUndoPolish(true);
      edit({
        title: String(next.title ?? draft.title).slice(0, 160),
        message: String(next.message ?? draft.message).slice(0, 4000),
      });
      toast.success("Polished. Review it, then send.");
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Could not polish the notice");
    } finally {
      setBusy(false);
    }
  };

  const undoPolish = () => {
    const prev = polishUndoRef.current;
    if (!prev) return;
    edit(prev);
    polishUndoRef.current = null;
    setCanUndoPolish(false);
  };

  /** A draft must exist before a file can hang off it. */
  const ensureDraft = async () => {
    if (draftIdRef.current) return draftIdRef.current;
    await persist();
    return draftIdRef.current;
  };

  // ---- files --------------------------------------------------------------
  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (draft.attachments.filter((a) => !isDerivativeAttachment(a)).length + files.length > MAX_NOTICE_FILES) {
      toast.error(`A notice can carry at most ${MAX_NOTICE_FILES} files`);
      return;
    }
    const bad = files.find((f) => !isAcceptedFile(f));
    if (bad) {
      toast.error(`"${bad.name}" is not a PDF, a photo (JPG, PNG, WebP) or a video (MP4, MOV)`);
      return;
    }
    // Video is capped far higher than everything else, so the cap is per file, not one rule.
    const capFor = (f) => (VIDEO_EXTENSIONS.some((e) => String(f.name || "").toLowerCase().endsWith(e)) ? MAX_NOTICE_VIDEO_BYTES : MAX_NOTICE_FILE_BYTES);
    const isVideo = (f) => VIDEO_EXTENSIONS.some((e) => String(f.name || "").toLowerCase().endsWith(e));
    // The server refuses a third video. Catching it here means the manager is told before
    // 75 MB has already been pushed to storage only to be rejected on arrival.
    const videosNow = draft.attachments.filter((a) => !isDerivativeAttachment(a) && String(a.mime || "").startsWith("video/")).length;
    if (videosNow + files.filter(isVideo).length > MAX_NOTICE_VIDEOS) {
      toast.error(`A notice can carry at most ${MAX_NOTICE_VIDEOS} videos`);
      return;
    }

    const big = files.find((f) => f.size > capFor(f));
    if (big) {
      toast.error(`"${big.name}" is larger than ${capFor(big) === MAX_NOTICE_VIDEO_BYTES ? "75 MB" : "10 MB"}`);
      return;
    }
    setBusy("upload");
    try {
      const id = await ensureDraft();
      let attachments = draft.attachments;
      for (const file of files) {
        setUploadPct(0);
        const stored = await uploadNoticeFile(file, (ev) => ev.total && setUploadPct(Math.round((ev.loaded / ev.total) * 100)));
        const res = await addNoticeAttachment(id, { key: stored.key, filename: file.name, size: file.size, mime: file.type });
        attachments = res?.data || attachments;
        // The controller answers with the whole attachments array; the one just pushed is last.
        const parentId = (Array.isArray(res?.data) ? res.data : []).slice(-1)[0]?.attachment_id;

        // A photo gets a web-sized twin for the email; a video gets a still with the play badge
        // drawn in. Both are best-effort: if either fails, the notice still sends without it.
        const derivative = isPhotoFile(file) ? await makeWebCopy(file) : isVideoFile(file) ? await makeVideoPoster(file) : null;
        if (derivative && parentId) {
          try {
            setUploadPct(0);
            const up = await uploadNoticeFile(derivative, (ev) => ev.total && setUploadPct(Math.round((ev.loaded / ev.total) * 100)));
            const dres = await addNoticeAttachment(id, {
              key: up.key,
              filename: derivative.name,
              size: derivative.size,
              mime: "image/jpeg",
              kind: isVideoFile(file) ? "poster" : "inline",
              poster_for: parentId,
            });
            attachments = dres?.data || attachments;
          } catch (err) {
            console.warn("[notices] derivative upload skipped:", err && err.message);
          }
        }
      }
      setDraft((d) => ({ ...d, attachments }));
      setSaveState("saved");
      setSavedAt(new Date());
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Upload failed");
    } finally {
      setBusy(false);
      setUploadPct(0);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeFile = async (attachmentId) => {
    try {
      const res = await removeNoticeAttachment(draftIdRef.current, attachmentId);
      setDraft((d) => ({ ...d, attachments: res?.data || [] }));
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not remove the file");
    }
  };

  const previewFile = async (attachmentId) => {
    try {
      const res = await getNoticeAttachmentUrl(draftIdRef.current, attachmentId);
      if (res?.data?.url) window.open(res.data.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error("Could not open the file");
    }
  };

  // ---- drafts strip ---------------------------------------------------------
  const continueDraft = (n) => {
    // A one-building draft continued here would be re-saved company-wide by autosave.
    if (allMode && n?.scope !== "company") {
      toast.info("This draft is for one building. Pick that property in the directory to continue it.");
      return;
    }
    dirtyRef.current = false;
    draftIdRef.current = n._id;
    setDraftId(n._id);
    setDraft({
      type: n.type || "General",
      title: n.title || "",
      message: n.message || "",
      attachments: n.attachments || [],
      audience: { ...emptyNotice().audience, ...(n.audience || {}) },
      options: { sms: !!n.options?.sms, require_acknowledgement: !!n.options?.require_acknowledgement, scheduled_at: null },
    });
    const recurrence = n.options?.recurrence;
    setWhen(recurrence ? "later" : "now");
    setScheduledAt(n.options?.scheduled_at ? toLocalInputValue(n.options.scheduled_at) : "");
    setScheduleMode(recurrence ? "recurring" : "once");
    setIntervalDays(String(recurrence?.interval_days || 4));
    setEventDate(recurrence?.event_at ? toLocalDateValue(recurrence.event_at) : "");
    setScope(n.scope === "company" ? "company" : "property");
    setPropertyIds(n.property_ids || []);
    setSaveState("saved");
    setSavedAt(n.updatedAt ? new Date(n.updatedAt) : null);
    polishUndoRef.current = null;
    setCanUndoPolish(false);
    setTab("compose");
  };

  // ---- send / test ------------------------------------------------------------
  /**
   * Everything that used to happen the moment a send returned: tell the page above, refresh
   * history, empty the compose tab and show what just went out. With the undo window it runs
   * thirty seconds later instead — or straight away for a scheduled send, which has no window.
   */
  const finishSend = useCallback(
    (data) => {
      if (onSent) onSent(data);
      setHistoryKey((k) => k + 1);
      resetCompose();
      setTab("history");
    },
    [onSent, resetCompose]
  );

  const validate = () => {
    if (!draft.title.trim()) return "Give the notice a title.";
    if (!draft.message.trim() && draft.attachments.length === 0) return "Add the notice file or write a message.";
    if (scope === "property" && !filterData.property_id) return "Pick a property and building first.";
    if (when === "later") {
      const d = scheduledAt ? new Date(scheduledAt) : null;
      if (!d || isNaN(d.getTime())) return "Pick a date and time to send.";
      if (d.getTime() < Date.now() + 60_000) return "The send time has to be in the future.";
      if (scheduleMode === "recurring") {
        const interval = Number(intervalDays);
        if (!Number.isInteger(interval) || interval < 1 || interval > 90) return "Choose a reminder frequency from 1 to 90 days.";
        if (!eventDate) return "Choose the event date when reminders should stop.";
        const plan = recurrencePlan(scheduledAt, eventDate, interval);
        if (!plan) return "The event date must be on or after the first reminder.";
        if (plan.count < 2) return "The event date must allow at least one repeat. Choose a later date or a shorter frequency.";
        if (plan.event.getTime() > Date.now() + 730 * 24 * 60 * 60 * 1000) return "The event date cannot be more than two years away.";
      }
    }
    return "";
  };

  const handleTest = async () => {
    const problem = !draft.title.trim() ? "Give the notice a title first." : "";
    if (problem) return toast.error(problem);
    setBusy("test");
    try {
      const id = await ensureDraft();
      const res = await sendNoticeTest(id);
      toast.success(res?.message || "Test sent");
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "The test could not be sent");
    } finally {
      setBusy(false);
    }
  };

  const handleSend = async () => {
    const problem = validate();
    if (problem) return toast.error(problem);
    const total = preview?.summary?.total || 0;
    const noEmail = preview?.summary?.without_email || 0;
    const repeating = when === "later" && scheduleMode === "recurring";
    const plan = repeating ? recurrencePlan(scheduledAt, eventDate, intervalDays) : null;
    const whenText = when === "later"
      ? repeating
        ? ` first on ${formatWhen(scheduledAt)}, then every ${intervalDays} days through ${formatDateOnly(eventDateEndIso(eventDate))}`
        : ` on ${formatWhen(scheduledAt)}`
      : " now";
    const ok = await Swal.fire({
      title: repeating ? "Schedule recurring reminders?" : when === "later" ? "Schedule this notice?" : "Send this notice?",
      html: `<div style="text-align:left;font-size:14px;line-height:1.5">
        <div><strong>${total}</strong> resident${total === 1 ? "" : "s"} will get <strong>${draft.title.replace(/</g, "&lt;")}</strong>${whenText}.</div>
        ${plan ? `<div style="margin-top:6px"><strong>${plan.count}</strong> reminder sends are planned. The resident list is refreshed before each send.</div>` : ""}
        ${noEmail ? `<div style="color:#b45309;margin-top:6px">${noEmail} of them have no email address and will be listed as missed.</div>` : ""}
        ${draft.options.sms ? `<div style="margin-top:6px">A text message with the link also goes to everyone with a mobile number.</div>` : ""}
      </div>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: repeating ? "Schedule reminders" : when === "later" ? "Schedule" : "Send",
      confirmButtonColor: "#0c447c",
    });
    if (!ok.isConfirmed) return;
    setBusy("send");
    try {
      const id = await ensureDraft();
      const schedule = when === "later"
        ? {
            scheduled_at: new Date(scheduledAt).toISOString(),
            ...(repeating
              ? {
                  recurrence: {
                    enabled: true,
                    interval_days: Number(intervalDays),
                    event_at: eventDateEndIso(eventDate),
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Edmonton",
                  },
                }
              : {}),
          }
        : null;
      const res = await sendNotice(id, schedule);
      toast.success(when === "later" ? (res?.message || "Notice scheduled") : "Notice queued. You can still undo before delivery.");
      if (when === "later") {
        // A scheduled notice can be cancelled from History for as long as it is waiting; there
        // is nothing to undo in the next thirty seconds.
        finishSend(res?.data);
        return;
      }
      // The server holds a "send now" for thirty seconds. The modal stays open for exactly
      // that long so the bar can stop it, then does what it always did.
      sentIdRef.current = id;
      sentDataRef.current = res?.data;
      setUndoing(true);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "The notice could not be sent");
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    if (busy) return;
    // Closing during the countdown lets the send stand — the server still dispatches it — so the
    // page above is told about it exactly as if the window had run out.
    if (undoing) {
      setUndoing(false);
      finishSend(sentDataRef.current);
    }
    onClose();
  };

  const plannedRecurrence = scheduleMode === "recurring"
    ? recurrencePlan(scheduledAt, eventDate, intervalDays)
    : null;
  const recurrenceIssue = scheduleMode !== "recurring" || (!scheduledAt && !eventDate)
    ? ""
    : !plannedRecurrence
      ? "The event date must be on or after the first reminder."
      : plannedRecurrence.count < 2
        ? "No repeat fits before this event date. Choose a later date or shorter frequency."
        : plannedRecurrence.event.getTime() > Date.now() + 730 * 24 * 60 * 60 * 1000
          ? "The event date cannot be more than two years away."
          : "";
  const recurrenceReady = scheduleMode !== "recurring" || (!!plannedRecurrence && !recurrenceIssue);
  const eventDateMin = scheduledAt?.slice(0, 10) || toLocalDateValue(new Date());
  const originalFiles = draft.attachments.filter((a) => !isDerivativeAttachment(a));
  const noticeFile = originalFiles[0];
  const extras = originalFiles.slice(1);
  const saveLabel = useMemo(() => {
    if (saveState === "saving") return "Saving…";
    if (saveState === "error") return "Could not save the draft";
    if (saveState === "saved" && savedAt) return `Draft saved ${savedAt.toLocaleTimeString("en-CA", { hour: "2-digit", minute: "2-digit" })}`;
    return meaningful ? "Unsaved changes" : "";
  }, [saveState, savedAt, meaningful]);

  return (
    <Modal isOpen={isOpen} toggle={close} size="xl" modalClassName="rn-modal" contentClassName="rn-content" centered scrollable>
      <div className="rn-head">
        <span className="rn-head-ic">
          <FeatherIcon icon="send" size={17} />
        </span>
        <div className="rn-head-text">
          <div className="rn-head-title">Notices</div>
          <div className="rn-head-sub">{allMode ? "All properties" : buildingLabel || "Pick a property to send notices"}</div>
        </div>
        <div className="rn-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={tab === "compose"} className={tab === "compose" ? "on" : ""} onClick={() => setTab("compose")}>
            New notice
          </button>
          <button type="button" role="tab" aria-selected={tab === "history"} className={tab === "history" ? "on" : ""} onClick={() => setTab("history")}>
            History
          </button>
        </div>
        <button type="button" className="rn-close" onClick={close} aria-label="Close">
          <FeatherIcon icon="x" size={18} />
        </button>
      </div>

      {tab === "history" ? (
        <div className="rn-body">
          <NoticeHistory
            filterData={filterData}
            scopeFilter={canCompany && scope === "company" ? "company" : "property"}
            refreshKey={historyKey}
            onContinueDraft={continueDraft}
            onDuplicated={continueDraft}
          />
        </div>
      ) : (
        <>
          <div className="rn-body">
            {drafts.filter((d) => d._id !== draftId).length > 0 && (
              <div className="rn-drafts">
                <FeatherIcon icon="file-text" size={13} />
                <span>Drafts:</span>
                {drafts
                  .filter((d) => d._id !== draftId)
                  .map((d) => (
                    <button key={d._id} type="button" className="rn-link" onClick={() => continueDraft(d)}>
                      {d.title || "Untitled"} · {formatWhen(d.updatedAt)}
                    </button>
                  ))}
              </div>
            )}

            {/* ---- 1. The notice ---- */}
            <section className="rn-section">
              <h3>
                <span className="n">1</span> The notice
              </h3>
              <TemplatePicker
                draft={draft}
                canDelete={canCompany}
                onApply={(t) =>
                  edit({
                    type: t.type || draft.type,
                    title: t.title || draft.title,
                    message: t.message || draft.message,
                    options: { ...draft.options, ...(t.options || {}) },
                  })
                }
              />
              <div className="rn-types" role="group" aria-label="Type">
                {NOTICE_TYPES.map((t) => (
                  <button key={t} type="button" className={`rn-chip${draft.type === t ? " on" : ""}`} aria-pressed={draft.type === t} onClick={() => edit({ type: t })} disabled={!!busy || undoing}>
                    <FeatherIcon icon={TYPE_ICON[t]} size={13} /> {t}
                  </button>
                ))}
              </div>
              <label className="rn-field">
                <span>Title</span>
                <input type="text" value={draft.title} maxLength={160} placeholder="e.g. Water shut-off on Tuesday, 9 to 11 am" onChange={(e) => edit({ title: e.target.value })} disabled={!!busy || undoing} />
              </label>
              <div className="rn-field">
                <span className="rn-field-head">
                  <label htmlFor="rn-message">Message</label>
                  <span className="rn-field-actions">
                    {canUndoPolish && (
                      <button type="button" className="rn-link" onClick={undoPolish} disabled={!!busy || undoing}>
                        Undo polish
                      </button>
                    )}
                    <button
                      type="button"
                      className="rn-polish"
                      onClick={handlePolish}
                      disabled={!!busy || undoing || (!draft.title.trim() && !draft.message.trim())}
                      title="Rewrite the title and message in clear, professional language"
                    >
                      {busy === "polish" ? <Spinner size="sm" /> : <FeatherIcon icon="edit-3" size={12} />}
                      {busy === "polish" ? "Polishing…" : "Polish"}
                    </button>
                  </span>
                </span>
                <textarea id="rn-message" rows={4} value={draft.message} maxLength={4000} placeholder="What residents need to know. This goes in the email above the attached notice." onChange={(e) => edit({ message: e.target.value })} disabled={!!busy || undoing} />
              </div>

              <div
                className={`rn-files${draft.attachments.length ? "" : " is-empty"}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.add("over");
                }}
                onDragLeave={(e) => e.currentTarget.classList.remove("over")}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove("over");
                  addFiles(e.dataTransfer.files);
                }}
              >
                {noticeFile ? (
                  <div className="rn-file is-notice">
                    <FeatherIcon icon="file-text" size={15} />
                    <span className="n">
                      {noticeFile.filename} <em>attached to the email</em>
                    </span>
                    <span className="s">{formatBytes(noticeFile.size)}</span>
                    <button type="button" className="rn-ib" title="Open" onClick={() => previewFile(noticeFile.attachment_id)} disabled={!!busy || undoing}>
                      <FeatherIcon icon="eye" size={13} />
                    </button>
                    <button type="button" className="rn-ib danger" title="Remove" onClick={() => removeFile(noticeFile.attachment_id)} disabled={!!busy || undoing}>
                      <FeatherIcon icon="trash-2" size={13} />
                    </button>
                  </div>
                ) : (
                  <button type="button" className="rn-dropzone" onClick={() => fileInputRef.current?.click()} disabled={!!busy || undoing}>
                    <span className="ic">
                      <FeatherIcon icon="upload-cloud" size={18} />
                    </span>
                    <span className="t">Drop the notice here, or browse</span>
                    <span className="b">PDF, photos (JPG, PNG, WebP) up to 10 MB, video (MP4, MOV) up to 75 MB</span>
                  </button>
                )}
                {extras.map((a) => (
                  <div key={a.attachment_id} className="rn-file">
                    <FeatherIcon icon="paperclip" size={14} />
                    <span className="n">{a.filename}</span>
                    <span className="s">{formatBytes(a.size)}</span>
                    <button type="button" className="rn-ib" title="Open" onClick={() => previewFile(a.attachment_id)} disabled={!!busy || undoing}>
                      <FeatherIcon icon="eye" size={13} />
                    </button>
                    <button type="button" className="rn-ib danger" title="Remove" onClick={() => removeFile(a.attachment_id)} disabled={!!busy || undoing}>
                      <FeatherIcon icon="trash-2" size={13} />
                    </button>
                  </div>
                ))}
                {noticeFile && originalFiles.length < MAX_NOTICE_FILES && (
                  <button type="button" className="rn-link rn-addfile" onClick={() => fileInputRef.current?.click()} disabled={!!busy || undoing}>
                    <FeatherIcon icon="plus" size={12} /> Add another file
                  </button>
                )}
                {busy === "upload" && (
                  <div className="rn-progress" aria-live="polite">
                    <Spinner size="sm" /> Uploading {uploadPct ? `${uploadPct}%` : ""}
                  </div>
                )}
                <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.mp4,.m4v,.mov" multiple hidden onChange={(e) => addFiles(e.target.files)} />
              </div>
            </section>

            {/* ---- 2. Who gets it ---- */}
            <section className="rn-section">
              <h3>
                <span className="n">2</span> Who gets it
              </h3>
              <AudiencePicker
                audience={draft.audience}
                onChange={editAudience}
                options={options}
                preview={preview}
                loading={previewLoading}
                scope={scope}
                canCompanyWide={canCompany}
                onScopeChange={(s) => {
                  dirtyRef.current = true;
                  setScope(s);
                }}
                properties={properties}
                selectedPropertyIds={propertyIds}
                onPropertyIdsChange={(ids) => {
                  dirtyRef.current = true;
                  setPropertyIds(ids);
                }}
                buildingLabel={filterData.building_name ? `This building` : `This property`}
                lockCompany={allMode}
                disabled={!!busy || undoing}
              />
            </section>

            {/* The panel needs a saved draft to preview against; autosave has made one by the
                time a title exists, which is exactly when the Preview button becomes enabled. */}
            {showPreview && draftId ? (
              <section className="rn-section">
                <NoticePreview noticeId={draftId} draft={draft} onClose={() => setShowPreview(false)} />
              </section>
            ) : null}

            {/* ---- 3. Delivery ---- */}
            <section className="rn-section">
              <h3>
                <span className="n">3</span> Delivery
              </h3>
              <div className="rn-delivery">
                <label className="rn-check">
                  <input type="checkbox" checked={!!draft.options.sms} onChange={(e) => edit({ options: { ...draft.options, sms: e.target.checked } })} disabled={!!busy || undoing} />
                  <span>
                    Also text residents who have a mobile number
                    <small>A short message with the link{preview?.summary ? ` · ${preview.summary.with_phone} of ${preview.summary.total} have a number` : ""}</small>
                  </span>
                </label>
                <label className="rn-check">
                  <input
                    type="checkbox"
                    checked={!!draft.options.require_acknowledgement}
                    onChange={(e) => edit({ options: { ...draft.options, require_acknowledgement: e.target.checked } })}
                    disabled={!!busy || undoing}
                  />
                  <span>
                    Require acknowledgement
                    <small>
                      Residents confirm they&apos;ve read it &mdash; a &quot;Got it&quot; button if there&apos;s a file to view, or automatically
                      when they open a message-only notice. History records who did. Use it for entry, inspection and rent notices.
                    </small>
                  </span>
                </label>
                <div className="rn-when" role="group" aria-label="When">
                  <button type="button" className={`rn-seg${when === "now" ? " on" : ""}`} aria-pressed={when === "now"} onClick={() => setWhen("now")} disabled={!!busy || undoing}>
                    <FeatherIcon icon="zap" size={13} /> Send now
                  </button>
                  <button type="button" className={`rn-seg${when === "later" ? " on" : ""}`} aria-pressed={when === "later"} onClick={() => setWhen("later")} disabled={!!busy || undoing}>
                    <FeatherIcon icon="clock" size={13} /> Schedule
                  </button>
                </div>
                {when === "later" && (
                  <div className="rn-schedule-panel">
                    <label className="rn-schedule-field">
                      <span>First reminder</span>
                      <input type="datetime-local" className="rn-datetime" value={scheduledAt} min={toLocalInputValue(new Date())} onChange={(e) => setScheduledAt(e.target.value)} aria-label="First reminder date and time" disabled={!!busy || undoing} />
                    </label>
                    <div className="rn-schedule-modes" role="radiogroup" aria-label="Reminder pattern">
                      <button type="button" role="radio" aria-checked={scheduleMode === "once"} className={`rn-schedule-mode${scheduleMode === "once" ? " on" : ""}`} onClick={() => setScheduleMode("once")} disabled={!!busy || undoing}>
                        <FeatherIcon icon="calendar" size={15} />
                        <span><strong>Send once</strong><small>One notice at the selected time</small></span>
                      </button>
                      <button type="button" role="radio" aria-checked={scheduleMode === "recurring"} className={`rn-schedule-mode${scheduleMode === "recurring" ? " on" : ""}`} onClick={() => setScheduleMode("recurring")} disabled={!!busy || undoing}>
                        <FeatherIcon icon="repeat" size={15} />
                        <span><strong>Repeat until event</strong><small>Keep reminding residents on a set cadence</small></span>
                      </button>
                    </div>
                    {scheduleMode === "recurring" && (
                      <div className="rn-recurrence-fields">
                        <label className="rn-schedule-field">
                          <span>Repeat every</span>
                          <span className="rn-number-field">
                            <input type="number" min="1" max="90" step="1" value={intervalDays} onChange={(e) => setIntervalDays(e.target.value)} aria-label="Repeat every number of days" disabled={!!busy || undoing} />
                            <small>days</small>
                          </span>
                        </label>
                        <label className="rn-schedule-field">
                          <span>Event date</span>
                          <input type="date" value={eventDate} min={eventDateMin} onChange={(e) => setEventDate(e.target.value)} aria-label="Event date" disabled={!!busy || undoing} />
                        </label>
                        <div className={`rn-recurrence-summary${recurrenceIssue ? " is-invalid" : ""}`} role="status">
                          <FeatherIcon icon="calendar" size={16} aria-hidden="true" />
                          {plannedRecurrence && !recurrenceIssue ? (
                            <span>
                              <strong>{plannedRecurrence.count} reminders planned</strong>
                              <small>First {formatWhen(plannedRecurrence.first)}, then every {plannedRecurrence.intervalDays} days through {formatDateOnly(plannedRecurrence.event)}.</small>
                            </span>
                          ) : recurrenceIssue ? (
                            <span>
                              <strong>Adjust the schedule</strong>
                              <small>{recurrenceIssue}</small>
                            </span>
                          ) : (
                            <span>
                              <strong>Set the cadence</strong>
                              <small>Choose a first reminder, frequency and event date. The event date is inclusive.</small>
                            </span>
                          )}
                        </div>
                        <p className="rn-recurrence-note">The current resident list is checked again before each reminder. No reminder is sent after the event date.</p>
                      </div>
                    )}
                  </div>
                )}
                <p className="rn-muted rn-delivery-note">
                  Each resident gets their own email from <strong>{userData?.company || "the company"}</strong> with the notice attached and a personal link that works for 60 days. Replies come to {userData?.email || "you"}.
                </p>
              </div>
            </section>
          </div>

          {confirming ? (
            <SendConfirm
              total={preview?.summary?.total || 0}
              units={preview?.summary?.units || 0}
              where={scope === "company" ? "Every property in the company" : [filterData.property_name, filterData.building_name].filter(Boolean).join(", ")}
              channels={draft.options.sms ? "email + text" : "email"}
              companyWide={scope === "company"}
              scheduled={when === "later"}
              recurring={when === "later" && scheduleMode === "recurring"}
              scheduleSummary={plannedRecurrence ? `${plannedRecurrence.count} reminders, every ${plannedRecurrence.intervalDays} days through ${formatDateOnly(plannedRecurrence.event)}` : ""}
              propertyCount={propertyIds?.length}
              busy={!!busy}
              onCancel={() => setConfirming(false)}
              onConfirm={() => {
                setConfirming(false);
                handleSend();
              }}
            />
          ) : null}

          {undoing ? (
            <UndoBar
              busy={busy === "undo"}
              deadline={sentDataRef.current?.options?.undoable_until}
              seconds={30}
              onDone={() => {
                setUndoing(false);
                finishSend(sentDataRef.current);
              }}
              onUndo={async () => {
                try {
                  setBusy("undo");
                  await undoNoticeSend(sentIdRef.current);
                  toast.success("Stopped. Nothing was sent.");
                  setUndoing(false);
                  setHistoryKey((k) => k + 1);
                } catch (e) {
                  toast.error(e?.response?.data?.message || "It had already gone out");
                  setUndoing(false);
                  finishSend(sentDataRef.current);
                } finally {
                  setBusy(false);
                }
              }}
            />
          ) : null}

          <div className="rn-foot">
            <span className={`rn-save ${saveState}`}>
              {saveState === "saving" ? <Spinner size="sm" /> : saveState === "saved" ? <FeatherIcon icon="check" size={13} /> : null} {saveLabel}
            </span>
            <div className="rn-foot-actions">
              <button type="button" className="rn-btn" onClick={handleTest} disabled={!!busy || !draft.title.trim()}>
                {busy === "test" ? <Spinner size="sm" /> : <FeatherIcon icon="mail" size={13} />} Send me a test
              </button>
              <button type="button" className="rn-btn" onClick={close} disabled={!!busy}>
                Close
              </button>
              <button
                type="button"
                className="rn-btn"
                onClick={() => setShowPreview((v) => !v)}
                disabled={!!busy || !draft.title.trim()}
                title={draft.title.trim() ? "See it as a resident will" : "Give the notice a title first"}
              >
                <FeatherIcon icon="eye" size={15} /> {showPreview ? "Hide preview" : "Preview"}
              </button>
              <button
                type="button"
                className="rn-btn rn-btn-primary"
                onClick={() => (needsConfirm(preview?.summary?.total) ? setConfirming(true) : handleSend())}
                disabled={!!busy || undoing || previewLoading || confirming || !meaningful || (preview?.summary?.total || 0) === 0 || (when === "later" && !scheduledAt) || (when === "later" && !recurrenceReady)}
              >
                {busy === "send" ? <Spinner size="sm" /> : <FeatherIcon icon={when === "later" && scheduleMode === "recurring" ? "repeat" : when === "later" ? "clock" : "send"} size={13} />}
                {when === "later" && scheduleMode === "recurring" ? "Schedule reminders" : when === "later" ? "Schedule" : "Send"}
                {preview?.summary?.total ? ` to ${preview.summary.total}` : ""}
              </button>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
};

export { SendNoticeModal };
